import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";
import type { LogEntry } from "../../API/Models";
import { formatRelativeTime } from "../../Helper/dates";
import { activityMetaFor } from "../../Helper/activityMeta";
import { groupActivity, isAutomated, type ActivityGroup } from "../../Realtime/activityStream";
import { avatarHue, PanelCard, userInitials } from "./dashboardShared";

const buildLogLink = (tags?: Array<string> | null): string | null => {
	if (!tags || tags.length === 0) return null;
	const slugTag = tags.find((t) => t?.startsWith("slug:"));
	if (!slugTag) return null;
	const slug = slugTag.slice(5);
	if (!slug) return null;
	const lang = tags.find((t) => t && /^[a-z]{2}$/.test(t)) ?? "fr";
	if (tags.includes("page")) return `/page/${lang}/${slug}`;
	return null;
};

const sameUser = (a?: string | null, b?: string) => (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();

/** "×N · il y a 2 min → il y a 38 min" — how many were folded and over what span (newest → oldest). */
const CountPill: React.FC<{ group: ActivityGroup; muted?: boolean }> = ({ group, muted }) => {
	if (group.count <= 1) return null;
	const span =
		group.latestAt && group.earliestAt && group.latestAt !== group.earliestAt
			? ` · ${formatRelativeTime(group.latestAt)} → ${formatRelativeTime(group.earliestAt)}`
			: "";
	return (
		<span
			className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
				muted ? "bg-slate-100 text-slate-500" : "bg-sky-50 text-sky-700"
			}`}
		>
			×{group.count}
			{span && <span className="font-normal opacity-80">{span}</span>}
		</span>
	);
};

/** A machine event (FX sync, auto-publish) — one calm muted line, normalized label. */
const AutomatedRow: React.FC<{ group: ActivityGroup }> = ({ group }) => {
	const meta = activityMetaFor(group.type);
	const label = meta?.label ?? group.message ?? "Mise à jour automatique";
	return (
		<li className="flex items-center gap-2.5 px-5 py-2">
			<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-400">
				{meta?.icon ?? <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />}
			</span>
			<span className="truncate text-[13px] font-medium text-slate-500">{label}</span>
			<span className="ml-auto flex items-center gap-2">
				<CountPill group={group} muted />
				{group.count <= 1 && <span className="text-[11px] text-slate-400">{formatRelativeTime(group.latestAt ?? group.createdAt)}</span>}
			</span>
		</li>
	);
};

/** A human action — full emphasis: actor avatar, bold message, sky rail when it's you. */
const HumanRow: React.FC<{ group: ActivityGroup; currentUser: string }> = ({ group, currentUser }) => {
	const link = buildLogLink(group.tags);
	const mine = sameUser(group.user, currentUser);
	const actor = mine ? "Vous" : group.user && group.user.trim() ? group.user : "SYSTEM";
	return (
		<li className={`px-5 py-2.5 ${mine ? "border-l-2 border-sky-400" : ""}`}>
			<div className="flex items-start gap-2.5">
				<span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${avatarHue(group.user ?? "")} ${mine ? "ring-2 ring-sky-400" : ""}`}>
					{userInitials(group.user ?? "")}
				</span>
				<div className="min-w-0 flex-1">
					<div className="flex items-start gap-2">
						{link ? (
							<Link to={link} className="block min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 hover:text-sky-700 hover:underline">
								{group.message || "Événement"} <FiExternalLink className="ml-0.5 inline h-3 w-3 text-slate-400" />
							</Link>
						) : (
							<p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800" title={group.message ?? ""}>
								{group.message || "Événement"}
							</p>
						)}
						<CountPill group={group} />
					</div>
					<div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
						<span className="font-medium text-slate-600">{actor}</span>
						<span>·</span>
						<span>{formatRelativeTime(group.latestAt ?? group.createdAt)}</span>
					</div>
				</div>
			</div>
		</li>
	);
};

export interface ChangesFeedProps {
	logs: LogEntry[];
	loading: boolean;
	currentUser: string;
	/** "me" → only the current admin's actions; "all" → everyone (automated events collapse to one line). */
	scope: "me" | "all";
	title: string;
	subtitle?: string;
	limit?: number;
	connected?: boolean;
	bodyClassName?: string;
}

/** Recent-changes feed. Human actions stand out; recurring machine events collapse to one calm line. */
export const ChangesFeed: React.FC<ChangesFeedProps> = ({ logs, loading, currentUser, scope, title, subtitle, limit = 8, connected, bodyClassName }) => {
	const groups = useMemo(() => {
		const base = scope === "me" ? logs.filter((l) => sameUser(l.user, currentUser)) : logs;
		return groupActivity(base).slice(0, limit);
	}, [logs, scope, currentUser, limit]);

	const emptyText = scope === "me" ? "Aucune modification de votre part." : "Aucune activité récente.";

	return (
		<PanelCard
			title={title}
			subtitle={subtitle}
			bodyClassName={bodyClassName ?? "max-h-[320px] overflow-auto"}
			action={
				connected !== undefined ? (
					<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-500">
						<span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
						{connected ? "Direct" : "Hors ligne"}
					</span>
				) : undefined
			}
		>
			{loading ? (
				<ul className="divide-y divide-slate-100">
					{Array.from({ length: 5 }).map((_, i) => (
						<li key={`cf-skel-${i}`} className="flex items-center gap-3 px-5 py-3">
							<span className="h-7 w-7 rounded-full bg-slate-100" />
							<div className="h-3 w-2/3 rounded bg-slate-100" />
						</li>
					))}
				</ul>
			) : groups.length === 0 ? (
				<div className="px-5 py-10 text-center text-sm text-slate-500">{emptyText}</div>
			) : (
				<ul className="divide-y divide-slate-100">
					{groups.map((group, index) =>
						isAutomated(group) ? (
							<AutomatedRow key={group.id ?? `cf-${index}`} group={group} />
						) : (
							<HumanRow key={group.id ?? `cf-${index}`} group={group} currentUser={currentUser} />
						)
					)}
				</ul>
			)}
		</PanelCard>
	);
};

export default ChangesFeed;
